import { Avatar } from "@chakra-ui/avatar";
import { Button } from "@chakra-ui/button";
import { useDisclosure } from "@chakra-ui/hooks";
import { BellIcon, ChevronDownIcon, MoonIcon, SunIcon } from "@chakra-ui/icons";
import { Input } from "@chakra-ui/input";
import { Box, Text } from "@chakra-ui/layout";
import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from "@chakra-ui/menu";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
} from "@chakra-ui/modal";
import { Spinner } from "@chakra-ui/spinner";
import { useToast } from "@chakra-ui/toast";
import { Tooltip } from "@chakra-ui/tooltip";
import { IconButton } from "@chakra-ui/react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import NotificationBadge, { Effect } from "react-notification-badge";
import { useHistory } from "react-router-dom";
import { ChatState } from "../../Context/ChatProvider";
import { getSender, decodeStoredContent, formatTimeAgo } from "../../config/ChatLogics";
import ChatLoading from "../ChatLoading";
import UserListItem from "../userAvatar/UserListItem";
import ProfileModal from "./ProfileModal";
import ChangePasswordModal from "./ChangePasswordModal";

function SideDrawer() {
  const [search, setSearch] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [notifFilter, setNotifFilter] = useState("all");
  const [readNotifIds, setReadNotifIds] = useState([]);
  const searchTimeout = useRef(null);

  const {
    setSelectedChat,
    user,
    setUser,
    notification,
    setNotification,
    chats,
    setChats,
    setBlockedIds,
    theme,
    toggleTheme,
  } = ChatState();

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();

  // Load the block list once per login so peer views can render
  // Block/Unblock state without an extra request per modal open.
  useEffect(() => {
    if (!user?.token) {
      setBlockedIds([]);
      return;
    }
    axios
      .get("/api/user/blocked", {
        headers: { Authorization: `Bearer ${user.token}` },
      })
      .then(({ data }) => {
        setBlockedIds(
          Array.isArray(data) ? data.map((u) => String(u._id || u)) : []
        );
      })
      .catch(() => {
        // block list is best-effort; actions re-check on demand
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const logoutHandler = () => {
    localStorage.removeItem("userInfo");
    setSelectedChat(null);
    setNotification([]);
    setChats([]);
    setBlockedIds([]);
    setUser(null);
    history.push("/");
  };

  const runSearch = async (query) => {
    const q = (query || "").trim();
    if (!q) {
      setSearchResult([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);

      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };

      const { data } = await axios.get(
        `/api/user?search=${encodeURIComponent(q)}`,
        config
      );

      setLoading(false);
      setSearchResult(data);
      setHasSearched(true);
    } catch (error) {
      setLoading(false);
      toast({
        title: "Error Occured!",
        description: "Failed to Load the Search Results",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    }
  };

  // Manual search (Go button / Enter key): run immediately.
  const handleSearch = async () => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = null;
    }
    if (!search.trim()) {
      toast({
        title: "Please Enter something in search",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top-left",
      });
      return;
    }
    runSearch(search);
  };

  // Live search: 300ms debounce, same pattern as the group modal.
  const handleSearchChange = (value) => {
    setSearch(value);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = null;
    }
    if (!value.trim()) {
      setSearchResult([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }
    searchTimeout.current = setTimeout(() => runSearch(value), 300);
  };

  // Drop any pending debounced search on unmount.
  useEffect(
    () => () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    },
    []
  );

  const accessChat = async (userId) => {
    console.log(userId);

    try {
      setLoadingChat(true);
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const { data } = await axios.post(`/api/chat`, { userId }, config);

      const currentChats = chats || [];
      if (!currentChats.find((c) => c._id === data._id))
        setChats([data, ...currentChats]);
      setSelectedChat(data);
      setLoadingChat(false);
      onClose();
    } catch (error) {
      setLoadingChat(false);
      toast({
        title: "Error fetching the chat",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    }
  };

  const clearNotifications = () => {
    setNotification([]);
    setReadNotifIds([]);
  };

  const isNotifUnread = (n) =>
    !(readNotifIds || []).some((id) => String(id) === String(n._id));
  const unreadNotifs = (notification || []).filter(isNotifUnread);
  const visibleNotifs =
    notifFilter === "unread" ? unreadNotifs : notification || [];

  const markAllRead = () => {
    setReadNotifIds((notification || []).map((n) => String(n._id)));
  };

  const openNotification = (notif) => {
    setSelectedChat(notif.chat);
    setNotification((notification || []).filter((n) => n !== notif));
    setReadNotifIds((prev) =>
      (prev || []).filter((id) => String(id) !== String(notif._id))
    );
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bg="var(--ch-bar)"
        backdropFilter="blur(16px)"
        color="var(--ch-text)"
        w="100%"
        p="5px 10px 5px 10px"
        borderBottom="1px solid var(--ch-item-border)"
        position="relative"
        zIndex={20}
      >
        <Tooltip label="Search Users to chat" hasArrow placement="bottom-end">
          <Button
            variant="ghost"
            color="var(--ch-text)"
            _hover={{ bg: "var(--ch-hover-ghost)" }}
            _active={{ bg: "var(--ch-item-hover)" }}
            onClick={onOpen}
          >
            <i className="fas fa-search"></i>
            <Text display={{ base: "none", md: "flex" }} px={4}>
              Search User
            </Text>
          </Button>
        </Tooltip>
        <Text
          fontSize="2xl"
          fontFamily="Sora"
          fontWeight="800"
          className="brand-gradient-text"
        >
          Meet-Up
        </Text>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip
            label={theme === "light" ? "Switch to dark" : "Switch to light"}
            hasArrow
            placement="bottom-end"
          >
            <IconButton
              aria-label="Toggle light and dark theme"
              icon={theme === "light" ? <MoonIcon /> : <SunIcon />}
              variant="ghost"
              color="var(--ch-dim)"
              _hover={{ bg: "var(--ch-hover-ghost)" }}
              onClick={toggleTheme}
              size="sm"
            />
          </Tooltip>
          <Menu>
            <MenuButton p={1}>
              <NotificationBadge
                count={unreadNotifs.length || 0}
                effect={Effect.SCALE}
              />
              <BellIcon fontSize="2xl" m={1} />
            </MenuButton>
            <MenuList
              px={2}
              py={2}
              bg="var(--ch-menu)"
              borderColor="var(--ch-panel-border)"
              boxShadow="var(--ch-menu-shadow)"
              minW="320px"
              maxW="92vw"
              maxH="380px"
              overflowY="auto"
              className="dark-scroll"
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                pb={2}
              >
                <Text
                  fontFamily="Sora"
                  fontWeight="800"
                  fontSize="12px"
                  letterSpacing="0.12em"
                  textTransform="uppercase"
                  color="var(--ch-text)"
                >
                  Notifications
                </Text>
                {(notification?.length || 0) > 0 ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Text
                      minW="24px"
                      h="24px"
                      px="7px"
                      borderRadius="full"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="12px"
                      fontWeight="800"
                      color="#fff"
                      bg="linear-gradient(135deg, #6e3aff, #00c2ff)"
                      boxShadow="0 4px 14px rgba(110,58,255,0.55)"
                    >
                      {unreadNotifs.length > 99 ? "99+" : unreadNotifs.length}
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      color="var(--ch-accent)"
                      _hover={{ bg: "var(--ch-hover-ghost)" }}
                      _focus={{ bg: "var(--ch-item-hover)" }}
                      onClick={clearNotifications}
                    >
                      Clear
                    </Button>
                  </Box>
                ) : null}
              </Box>
              {(notification?.length || 0) > 0 && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={2}
                  pb={2}
                  gap={2}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {["all", "unread"].map((f) => (
                      <Button
                        key={f}
                        size="xs"
                        variant={notifFilter === f ? "solid" : "ghost"}
                        colorScheme={notifFilter === f ? "cyan" : undefined}
                        color={notifFilter === f ? "#06202a" : "#e2e8f0"}
                        _hover={{ bg: "rgba(255,255,255,0.1)" }}
                        onClick={() => setNotifFilter(f)}
                        textTransform="capitalize"
                      >
                        {f} ({f === "unread" ? unreadNotifs.length : notification.length})
                      </Button>
                    ))}
                  </Box>
                  {unreadNotifs.length > 0 && (
                    <Button
                      size="xs"
                      variant="ghost"
                      color="var(--ch-dim)"
                      _hover={{ bg: "var(--ch-hover-ghost)" }}
                      onClick={markAllRead}
                    >
                      Mark all read
                    </Button>
                  )}
                </Box>
              )}
              {!(notification?.length) && (
                <Box textAlign="center" py={6} px={4}>
                  <BellIcon fontSize="3xl" color="var(--ch-faint)" />
                  <Text mt={2} fontWeight="700" color="var(--ch-text)">
                    All caught up
                  </Text>
                  <Text fontSize="xs" color="var(--ch-faint)">
                    New messages will land here.
                  </Text>
                </Box>
              )}
              {notifFilter === "unread" &&
                (notification?.length || 0) > 0 &&
                visibleNotifs.length === 0 && (
                  <Box textAlign="center" py={6} px={4}>
                    <Text mt={2} fontWeight="700" color="var(--ch-text)" fontSize="sm">
                      No unread notifications
                    </Text>
                    <Text fontSize="xs" color="var(--ch-faint)">
                      Switch to All to see read ones.
                    </Text>
                  </Box>
                )}
              {visibleNotifs?.map((notif) => {
                const isGroup = !!notif.chat?.isGroupChat;
                const title = isGroup
                  ? notif.chat?.chatName || "Group"
                  : notif.sender?.name ||
                    getSender(user, notif.chat?.users) ||
                    "Unknown";
                return (
                  <MenuItem
                    key={notif._id}
                    display="flex"
                    alignItems="center"
                    w="100%"
                    borderRadius="12px"
                    mb={1}
                    opacity={isNotifUnread(notif) ? 1 : 0.55}
                    _hover={{ bg: "var(--ch-item-hover)" }}
                    _focus={{ bg: "var(--ch-item-hover)" }}
                    onClick={() => openNotification(notif)}
                  >
                    <Avatar
                      size="sm"
                      mr={3}
                      name={title}
                      src={notif.sender?.pic}
                    />
                    <Box flex="1" minW={0}>
                      <Box
                        display="flex"
                        alignItems="baseline"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Text fontWeight="700" fontSize="sm" isTruncated>
                          {title}
                        </Text>
                        <Text
                          fontSize="10px"
                          color="var(--ch-faint)"
                          flexShrink={0}
                        >
                          {formatTimeAgo(notif.createdAt)}
                        </Text>
                      </Box>
                      <Text
                        fontSize="xs"
                        color="var(--ch-dim)"
                        isTruncated
                      >
                        {isGroup && notif.sender?.name
                          ? `${notif.sender.name}: `
                          : ""}
                        {decodeStoredContent(notif.content)}
                      </Text>
                    </Box>
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={isNotifUnread(notif) ? "#22d3ee" : "transparent"}
                      boxShadow={
                        isNotifUnread(notif)
                          ? "0 0 8px rgba(34,211,238,0.9)"
                          : "none"
                      }
                      ml={2}
                      flexShrink={0}
                    />
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
          <Menu>
            <MenuButton
              as={Button}
              bg="transparent"
              _hover={{ bg: "var(--ch-hover-ghost)" }}
              _active={{ bg: "var(--ch-item-hover)" }}
              rightIcon={<ChevronDownIcon />}
            >
              <Avatar
                size="sm"
                cursor="pointer"
                name={user?.name}
                src={user?.pic}
              />
            </MenuButton>
            <MenuList
              bg="var(--ch-menu)"
              borderColor="var(--ch-panel-border)"
              boxShadow="var(--ch-menu-shadow)"
            >
              <ProfileModal user={user}>
                <MenuItem
                  _hover={{ bg: "var(--ch-item-hover)" }}
                  _focus={{ bg: "var(--ch-item-hover)" }}
                >
                  My Profile
                </MenuItem>{" "}
              </ProfileModal>
              <ChangePasswordModal>
                <MenuItem
                  _hover={{ bg: "var(--ch-item-hover)" }}
                  _focus={{ bg: "var(--ch-item-hover)" }}
                >
                  Change Password
                </MenuItem>
              </ChangePasswordModal>
              <MenuDivider borderColor="var(--ch-item-border)" />
              <MenuItem
                _hover={{ bg: "var(--ch-item-hover)" }}
                _focus={{ bg: "var(--ch-item-hover)" }}
                onClick={logoutHandler}
              >
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </div>
      </Box>

      <Drawer placement="left" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent bg="var(--ch-drawer)" color="var(--ch-text)">
          <DrawerHeader
            borderBottomWidth="1px"
            borderBottomColor="var(--ch-item-border)"
            fontFamily="Sora"
          >
            Search Users
          </DrawerHeader>
          <DrawerBody>
            <Box display="flex" pb={2}>
              <Input
                placeholder="Search by name or email"
                mr={2}
                className="chat-input"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
              <Button className="chat-btn-primary" onClick={handleSearch}>
                Go
              </Button>
            </Box>
            {loading ? (
              <ChatLoading />
            ) : (
              <>
                {hasSearched && (
                  <Text fontSize="xs" color="var(--ch-faint)" mb={1}>
                    {searchResult.length === 0
                      ? "No users found"
                      : `${searchResult.length} user${searchResult.length === 1 ? "" : "s"} found`}
                  </Text>
                )}
                <Box
                  maxH="320px"
                  overflowY="auto"
                  className="dark-scroll"
                  display="flex"
                  flexDir="column"
                  gap={1}
                >
                  {searchResult?.map((user) => (
                    <UserListItem
                      key={user._id}
                      user={user}
                      handleFunction={() => accessChat(user._id)}
                    />
                  ))}
                </Box>
                {hasSearched && searchResult.length === 0 && (
                  <Box textAlign="center" py={6} px={4}>
                    <Text fontWeight="700" color="var(--ch-text)" fontSize="sm">
                      No users found
                    </Text>
                    <Text fontSize="xs" color="var(--ch-faint)" mt={1}>
                      Try a different name or email.
                    </Text>
                  </Box>
                )}
              </>
            )}
            {loadingChat && <Spinner ml="auto" display="flex" />}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default SideDrawer;
